import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuth'
import { api } from '../lib/api'

export default function Settings() {
  const navigate = useNavigate()
  const savedTimerRef = useRef<number | undefined>(undefined)
  const { user, logout } = useAuthStore()
  const [alpacaKey,    setAlpacaKey]    = useState('')
  const [alpacaSecret, setAlpacaSecret] = useState('')
  const [saving,   setSaving]   = useState(false)
  const [curPw,    setCurPw]    = useState('')
  const [newPw,    setNewPw]    = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwMsg,    setPwMsg]    = useState('')
  const [pwErr,    setPwErr]    = useState('')
  const [pwStep,   setPwStep]   = useState<'fields'|'code'|'newpw'>('fields')
  const [pwToken,  setPwToken]  = useState('')
  const [pwCode,   setPwCode]   = useState('')
  const [saved,    setSaved]    = useState(false)
  const [saveErr,  setSaveErr]  = useState('')
  const [fbMsg,    setFbMsg]    = useState('')
  const [fbSent,   setFbSent]   = useState(false)

  async function sendFeedback() {
    if (fbMsg.trim().length < 3) return
    try {
      await api.market.feedback(fbMsg.trim(), 'settings')
      setFbSent(true); setFbMsg('')
      setTimeout(() => setFbSent(false), 4000)
    } catch { /* ignore */ }
  }

  // ── Mr. Roboto settings ─────────────────────────────────────────────────────
  const [robo, setRobo] = useState<{
    enabled: boolean; tradeAmount: number; minSurgeRatio: number;
    topResultOnly: boolean; maxPositions: number; stopLossPct: number;
    takeProfitPct: number; forceCloseEod: boolean; maxSpreadCents: number|null;
  }>({
    enabled:       false,
    tradeAmount:   500,
    minSurgeRatio: 2.0,
    topResultOnly: true,
    maxPositions:  3,
    stopLossPct:   5.0,
    takeProfitPct: 15.0,
    forceCloseEod: true,
    maxSpreadCents: null,
  })
  const [roboSaving, setRoboSaving] = useState(false)
  const [roboSaved,  setRoboSaved]  = useState(false)
  const [roboErr,    setRoboErr]    = useState('')

  useEffect(() => {
    api.robotrader.getSettings().then((s: any) => setRobo(s)).catch(() => {})
  }, [])

  async function saveRoboSettings() {
    setRoboSaving(true); setRoboErr(''); setRoboSaved(false)
    try {
      await api.robotrader.saveSettings(robo)
      setRoboSaved(true)
      setTimeout(() => setRoboSaved(false), 3000)
    } catch (e: any) {
      setRoboErr(e.message)
    } finally {
      setRoboSaving(false)
    }
  }

  async function changePasswordRequest() {
    if (!curPw) { setPwErr('Enter your current password'); return }
    setPwSaving(true); setPwErr(''); setPwMsg('')
    try {
      const res = await api.auth.changePasswordRequest(curPw)
      setPwToken(res.token)
      setPwStep('code')
    } catch (e: any) {
      setPwErr(e.message)
    } finally {
      setPwSaving(false)
    }
  }

  async function changePasswordVerify() {
    if (pwCode.length !== 6) { setPwErr('Enter the 6-digit code from your email'); return }
    setPwSaving(true); setPwErr('')
    try {
      await api.auth.changePasswordConfirm(pwToken, pwCode)
      setPwStep('newpw')
      setPwErr('')
    } catch (e: any) {
      setPwErr(e.message)
    } finally {
      setPwSaving(false)
    }
  }

  async function changePasswordApply() {
    if (!newPw || !confirmPw) { setPwErr('All fields required'); return }
    if (newPw !== confirmPw) { setPwErr('New passwords do not match'); return }
    if (newPw.length < 8) { setPwErr('New password must be at least 8 characters'); return }
    setPwSaving(true); setPwErr('')
    try {
      await api.auth.changePasswordApply(pwToken, newPw)
      setPwMsg('Password changed successfully')
      setCurPw(''); setNewPw(''); setConfirmPw(''); setPwCode(''); setPwToken('')
      setPwStep('fields')
    } catch (e: any) {
      setPwErr(e.message)
    } finally {
      setPwSaving(false)
    }
  }

  async function saveAlpacaKeys() {
    if (!alpacaKey || !alpacaSecret) return
    setSaving(true); setSaveErr(''); setSaved(false)
    try {
      await api.settings.saveAlpacaKeys({ key: alpacaKey, secret: alpacaSecret })
      setSaved(true)
      setAlpacaKey('')
      setAlpacaSecret('')
      const t = window.setTimeout(() => setSaved(false), 3000); savedTimerRef.current = t
    } catch (e: any) {
      // Surface a clear warning if user entered live trading keys instead of paper keys
      if (e.message?.includes('live Alpaca trading keys') || e.message?.includes('live_keys')) {
        setSaveErr('⚠ Live trading keys detected. These keys would trade with real money. You must use Alpaca paper account keys here. In Alpaca: switch to Paper Trading → API Keys → generate paper keys.')
      } else if (e.message?.includes('invalid_keys') || e.message?.includes('authenticate')) {
        setSaveErr('Invalid keys — could not authenticate with Alpaca. Check your key and secret and try again.')
      } else {
        setSaveErr(e.message)
      }
    } finally {
      setSaving(false)
    }
  }

  const inputStyle = {
    background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2,
    padding: '10px 13px', color: 'var(--text)', fontFamily: 'Syne Mono',
    fontSize: '0.75rem', outline: 'none', width: '100%',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 560 }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Settings</h1>
        <div className="eyebrow">Account & preferences</div>
      </div>

      {/* Account info */}
      {[
        { title: 'Account', rows: [{ label: 'Email', val: user?.email }, { label: 'Name', val: user?.name || '—' }] },
      ].map(section => (
        <div key={section.title} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--edge)' }}>
            <span className="eyebrow">{section.title}</span>
          </div>
          {section.rows.map(row => (
            <div key={row.label} style={{ padding: '12px 16px', borderBottom: '1px solid var(--edge)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: 'Space Grotesk', fontSize: '0.82rem', color: 'var(--muted)' }}>{row.label}</span>
              <span style={{ fontFamily: 'Syne Mono', fontSize: '0.75rem', color: 'var(--text)' }}>{row.val}</span>
            </div>
          ))}
        </div>
      ))}

      {/* Alpaca paper trading */}
      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--edge)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="eyebrow">Paper Trading — Alpaca</span>
          <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer"
            style={{ fontFamily: 'Syne Mono', fontSize: '0.58rem', color: 'var(--cash)', textDecoration: 'none' }}>
            Get free keys →
          </a>
        </div>

        <div style={{ padding: '16px' }}>
          <div style={{ background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)', borderRadius: 3, padding: '10px 13px', marginBottom: 14, fontFamily: 'Syne Mono', fontSize: '0.6rem', color: 'var(--muted)', lineHeight: 1.7 }}>
            Connect your own free Alpaca paper trading account to track a personal paper portfolio.
            Go to <strong style={{ color: 'var(--cash)' }}>alpaca.markets</strong> → sign up free → Paper Trading → API Keys → Generate New Key.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '0.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '0.1em' }}>API KEY ID</div>
              <input
                type="text"
                value={alpacaKey}
                onChange={e => setAlpacaKey(e.target.value)}
                placeholder="PKXXXXXXXXXXXXXXXX"
                style={inputStyle}
              />
            </div>
            <div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '0.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '0.1em' }}>SECRET KEY</div>
              <input
                type="password"
                value={alpacaSecret}
                onChange={e => setAlpacaSecret(e.target.value)}
                placeholder="••••••••••••••••••••••••••••••••••••••••"
                style={inputStyle}
              />
            </div>

            {saveErr && (
              <div style={{ fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--red)', background: 'rgba(224,92,92,.07)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 10px' }}>{saveErr}</div>
            )}

            <button
              onClick={saveAlpacaKeys}
              disabled={saving || !alpacaKey || !alpacaSecret}
              style={{
                padding: '10px', background: saved ? 'rgba(133,196,124,.15)' : 'var(--cash)',
                color: saved ? 'var(--cash)' : 'var(--ink)', border: saved ? '1px solid var(--cash)' : 'none',
                borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '0.78rem',
                cursor: alpacaKey && alpacaSecret ? 'pointer' : 'not-allowed',
                opacity: saving || (!alpacaKey || !alpacaSecret) ? .5 : 1,
                transition: 'all 0.2s',
              }}
            >
              {saved ? '✓ Keys saved — Trading tab is now active' : saving ? 'Saving...' : 'Save Alpaca Keys →'}
            </button>

            <div style={{ fontFamily: 'Syne Mono', fontSize: '0.56rem', color: 'var(--muted)', opacity: 0.6 }}>
              Keys are encrypted and stored securely. Only paper trading keys are supported — no real money.
            </div>
          </div>
        </div>
      </div>

      {/* Mr. Roboto */}
      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--edge)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="eyebrow">🤖 Mr. Roboto — Paper Trading</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
              {robo.enabled ? 'ACTIVE' : 'INACTIVE'}
            </span>
            <button
              onClick={() => setRobo(r => ({ ...r, enabled: !r.enabled }))}
              style={{
                width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                background: robo.enabled ? 'var(--cash)' : 'var(--edge)',
                position: 'relative', transition: 'background .2s',
              }}>
              <div style={{
                width: 16, height: 16, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 3,
                left: robo.enabled ? 21 : 3, transition: 'left .2s',
              }} />
            </button>
          </div>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)', borderRadius: 3, padding: '10px 13px', fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', lineHeight: 1.7 }}>
            When active, Mr. Roboto auto-places paper bracket orders on Alpaca after each surge scan — buys on detection, with automatic stop loss and take profit.
          </div>

          {/* Trade Amount */}
          <div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '.1em' }}>TRADE AMOUNT ($)</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[100, 250, 500, 1000, 2500].map(v => (
                <button key={v} onClick={() => setRobo(r => ({ ...r, tradeAmount: v }))}
                  style={{ padding: '6px 12px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.65rem', cursor: 'pointer', border: '1px solid var(--edge)', background: robo.tradeAmount === v ? 'var(--cash)' : 'transparent', color: robo.tradeAmount === v ? 'var(--ink)' : 'var(--muted)' }}>
                  ${v.toLocaleString()}
                </button>
              ))}
              <input type="number" min={10} max={100000} value={robo.tradeAmount}
                onChange={e => setRobo(r => ({ ...r, tradeAmount: parseFloat(e.target.value) || 500 }))}
                style={{ ...inputStyle, width: 90 }} />
            </div>
          </div>

          {/* Min Surge Ratio */}
          <div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '.1em' }}>MIN SURGE RATIO TO TRIGGER</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[1.5, 2, 3, 5, 10].map(v => (
                <button key={v} onClick={() => setRobo(r => ({ ...r, minSurgeRatio: v }))}
                  style={{ padding: '6px 12px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.65rem', cursor: 'pointer', border: '1px solid var(--edge)', background: robo.minSurgeRatio === v ? 'var(--cash)' : 'transparent', color: robo.minSurgeRatio === v ? 'var(--ink)' : 'var(--muted)' }}>
                  {v}×
                </button>
              ))}
            </div>
          </div>

          {/* Stop Loss */}
          <div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '.1em' }}>STOP LOSS (% BELOW ENTRY)</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[3, 5, 7, 10, 15].map(v => (
                <button key={v} onClick={() => setRobo(r => ({ ...r, stopLossPct: v }))}
                  style={{ padding: '6px 12px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.65rem', cursor: 'pointer', border: '1px solid var(--edge)', background: robo.stopLossPct === v ? 'var(--red)' : 'transparent', color: robo.stopLossPct === v ? '#fff' : 'var(--muted)' }}>
                  -{v}%
                </button>
              ))}
              <input type="number" min={0.5} max={50} step={0.5} value={robo.stopLossPct}
                onChange={e => setRobo(r => ({ ...r, stopLossPct: parseFloat(e.target.value) || 5 }))}
                style={{ ...inputStyle, width: 70 }} />
            </div>
          </div>

          {/* Take Profit */}
          <div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '.1em' }}>TAKE PROFIT (% ABOVE ENTRY)</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[10, 15, 20, 30, 50].map(v => (
                <button key={v} onClick={() => setRobo(r => ({ ...r, takeProfitPct: v }))}
                  style={{ padding: '6px 12px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.65rem', cursor: 'pointer', border: '1px solid var(--edge)', background: robo.takeProfitPct === v ? 'var(--cash)' : 'transparent', color: robo.takeProfitPct === v ? 'var(--ink)' : 'var(--muted)' }}>
                  +{v}%
                </button>
              ))}
              <input type="number" min={1} max={200} step={1} value={robo.takeProfitPct}
                onChange={e => setRobo(r => ({ ...r, takeProfitPct: parseFloat(e.target.value) || 15 }))}
                style={{ ...inputStyle, width: 70 }} />
            </div>
          </div>

          {/* Max Positions + Entry Mode */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '.1em' }}>MAX OPEN POSITIONS</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[1, 2, 3, 5, 10].map(v => (
                  <button key={v} onClick={() => setRobo(r => ({ ...r, maxPositions: v }))}
                    style={{ padding: '6px 10px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.65rem', cursor: 'pointer', border: '1px solid var(--edge)', background: robo.maxPositions === v ? 'var(--cash)' : 'transparent', color: robo.maxPositions === v ? 'var(--ink)' : 'var(--muted)' }}>
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '.1em' }}>ENTRY MODE</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setRobo(r => ({ ...r, topResultOnly: true }))}
                  style={{ padding: '6px 10px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.62rem', cursor: 'pointer', border: '1px solid var(--edge)', background: robo.topResultOnly ? 'var(--cash)' : 'transparent', color: robo.topResultOnly ? 'var(--ink)' : 'var(--muted)', whiteSpace: 'nowrap' }}>
                  Top result only
                </button>
                <button onClick={() => setRobo(r => ({ ...r, topResultOnly: false }))}
                  style={{ padding: '6px 10px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.62rem', cursor: 'pointer', border: '1px solid var(--edge)', background: !robo.topResultOnly ? 'var(--cash)' : 'transparent', color: !robo.topResultOnly ? 'var(--ink)' : 'var(--muted)', whiteSpace: 'nowrap' }}>
                  All qualifying
                </button>
              </div>
            </div>
          </div>

          {/* Force close EOD */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => setRobo(r => ({ ...r, forceCloseEod: !r.forceCloseEod }))}
              style={{ width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', background: robo.forceCloseEod ? 'var(--cash)' : 'var(--edge)', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: robo.forceCloseEod ? 19 : 3, transition: 'left .2s' }} />
            </button>
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>
              Force-close all positions at 3:50pm ET (avoid overnight holds)
            </span>
          </div>

          {/* Spread filter */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', letterSpacing: '.06em' }}>MAX SPREAD FILTER</span>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {([null, 1, 2, 5] as const).map(v => (
                <button key={String(v)} onClick={() => setRobo(r => ({ ...r, maxSpreadCents: v }))} style={{
                  padding: '5px 12px',
                  background: robo.maxSpreadCents === v ? 'var(--cash)' : 'transparent',
                  color:      robo.maxSpreadCents === v ? 'var(--ink)' : 'var(--muted)',
                  border:     `1px solid ${robo.maxSpreadCents === v ? 'var(--cash)' : 'var(--edge)'}`,
                  borderRadius: 2, fontFamily: 'Syne', fontWeight: 700, fontSize: '.64rem', cursor: 'pointer',
                }}>{v === null ? 'OFF' : `<${v}¢`}</button>
              ))}
            </div>
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', opacity: .7 }}>
              Skip trades where bid/ask spread exceeds this threshold — avoids illiquid fills
            </span>
          </div>

          {/* R:R preview */}
          <div style={{ background: 'var(--stamp)', border: '1px solid var(--edge)', borderRadius: 3, padding: '10px 13px', fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <span>Risk/Reward: <strong style={{ color: 'var(--text)' }}>1 : {(robo.takeProfitPct / robo.stopLossPct).toFixed(1)}</strong></span>
            <span>Max loss/trade: <strong style={{ color: 'var(--red)' }}>-${(robo.tradeAmount * robo.stopLossPct / 100).toFixed(0)}</strong></span>
            <span>Max gain/trade: <strong style={{ color: 'var(--cash)' }}>+${(robo.tradeAmount * robo.takeProfitPct / 100).toFixed(0)}</strong></span>
          </div>

          {roboErr && <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--red)', background: 'rgba(224,92,92,.07)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 10px' }}>{roboErr}</div>}

          <button onClick={saveRoboSettings} disabled={roboSaving}
            style={{ padding: '10px', background: roboSaved ? 'rgba(133,196,124,.15)' : 'var(--cash)', color: roboSaved ? 'var(--cash)' : 'var(--ink)', border: roboSaved ? '1px solid var(--cash)' : 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.78rem', cursor: roboSaving ? 'not-allowed' : 'pointer', opacity: roboSaving ? .5 : 1, transition: 'all .2s' }}>
            {roboSaved ? '✓ Mr. Roboto settings saved' : roboSaving ? 'Saving...' : 'Save Mr. Roboto Settings →'}
          </button>
        </div>
      </div>

      {/* Change Password */}
      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--edge)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="eyebrow">Change Password</span>
          {pwStep !== 'fields' && (
            <button onClick={() => { setPwStep('fields'); setPwCode(''); setNewPw(''); setConfirmPw(''); setPwErr('') }}
              style={{ background: 'none', border: 'none', color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.6rem', cursor: 'pointer' }}>← back</button>
          )}
        </div>
        <div style={{ padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>

          {pwStep === 'fields' && (<>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)', lineHeight: 1.7 }}>
              Verify your identity first — a code will be sent to your email.
            </div>
            <div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '0.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '0.1em' }}>CURRENT PASSWORD</div>
              <input type="password" value={curPw} onChange={e => setCurPw(e.target.value)}
                placeholder="Current password"
                onKeyDown={e => e.key === 'Enter' && changePasswordRequest()}
                style={inputStyle} />
            </div>
            {pwErr && <div style={{ fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--red)', background: 'rgba(224,92,92,.07)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 10px' }}>{pwErr}</div>}
            {pwMsg && <div style={{ fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--cash)', background: 'rgba(133,196,124,.07)', border: '1px solid var(--cash-dim)', borderRadius: 2, padding: '8px 10px' }}>✓ {pwMsg}</div>}
            <button onClick={changePasswordRequest} disabled={pwSaving} style={{
              padding: '10px', background: 'var(--stamp)', color: 'var(--text)',
              border: '1px solid var(--edge)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
              fontSize: '0.78rem', cursor: 'pointer', opacity: pwSaving ? .5 : 1,
            }}>
              {pwSaving ? 'Sending code...' : 'Send Verification Code →'}
            </button>
          </>)}

          {pwStep === 'code' && (<>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)', lineHeight: 1.7 }}>
              A 6-digit code was sent to your email. Enter it to continue.
            </div>
            {pwErr && <div style={{ fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--red)', background: 'rgba(224,92,92,.07)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 10px' }}>{pwErr}</div>}
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              placeholder="000000" value={pwCode}
              onChange={e => setPwCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              onKeyDown={e => e.key === 'Enter' && changePasswordVerify()}
              style={{ ...inputStyle, fontSize: '1.6rem', letterSpacing: '0.4em', textAlign: 'center', padding: '14px' }}
            />
            <button onClick={changePasswordVerify} disabled={pwSaving || pwCode.length !== 6} style={{
              padding: '10px', background: 'var(--cash)', color: 'var(--ink)',
              border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
              fontSize: '0.78rem', cursor: pwCode.length === 6 ? 'pointer' : 'not-allowed',
              opacity: pwSaving || pwCode.length !== 6 ? .5 : 1,
            }}>
              {pwSaving ? 'Verifying...' : 'Verify Code →'}
            </button>
          </>)}

          {pwStep === 'newpw' && (<>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--cash)', lineHeight: 1.7 }}>
              ✓ Identity verified. Choose your new password.
            </div>
            <div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '0.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '0.1em' }}>NEW PASSWORD</div>
              <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                placeholder="New password (min 8 chars)" style={inputStyle} />
            </div>
            <div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '0.6rem', color: 'var(--muted)', marginBottom: 5, letterSpacing: '0.1em' }}>CONFIRM NEW PASSWORD</div>
              <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                placeholder="Confirm new password"
                onKeyDown={e => e.key === 'Enter' && changePasswordApply()}
                style={inputStyle} />
            </div>
            {pwErr && <div style={{ fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--red)', background: 'rgba(224,92,92,.07)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 10px' }}>{pwErr}</div>}
            <button onClick={changePasswordApply} disabled={pwSaving} style={{
              padding: '10px', background: 'var(--cash)', color: 'var(--ink)',
              border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
              fontSize: '0.78rem', cursor: 'pointer', opacity: pwSaving ? .5 : 1,
            }}>
              {pwSaving ? 'Updating...' : 'Update Password →'}
            </button>
          </>)}

        </div>
      </div>

      {/* Feedback / report an issue */}
      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--edge)' }}>
          <span className="eyebrow">Report an Issue / Feedback</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', lineHeight: 1.6 }}>
            Something broken or an idea? It's logged and feeds the improvement loop.
          </div>
          <textarea value={fbMsg} onChange={e => setFbMsg(e.target.value)} rows={3}
            placeholder="What's wrong / what would you improve?"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'Syne Mono', fontSize: '.7rem' }} />
          <button onClick={sendFeedback} disabled={fbMsg.trim().length < 3} style={{
            padding: '10px', background: fbSent ? 'rgba(133,196,124,.15)' : 'var(--cash)',
            color: fbSent ? 'var(--cash)' : 'var(--ink)', border: fbSent ? '1px solid var(--cash)' : 'none',
            borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.78rem',
            cursor: fbMsg.trim().length < 3 ? 'not-allowed' : 'pointer', opacity: fbMsg.trim().length < 3 ? .5 : 1,
          }}>
            {fbSent ? '✓ Thanks — logged' : 'Send Feedback →'}
          </button>
        </div>
      </div>

      {/* Logout — visible on all screens, essential on mobile where sidebar user section is hidden */}
      <button
        onClick={() => { logout(); navigate('/') }}
        style={{
          padding: '12px 24px', border: '1px solid rgba(224,92,92,.3)',
          background: 'transparent', color: 'var(--red)', borderRadius: 2,
          fontFamily: 'Syne', fontWeight: 700, fontSize: '0.78rem',
          cursor: 'pointer', width: 'fit-content',
        }}>
        Log Out
      </button>
    </div>
  )
}
